<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { 
  Plus, Search, MessageSquare, Settings, Video, User, 
  Scale, Send, Menu, X
} from 'lucide-vue-next'
import { Room, RoomEvent } from 'livekit-client'
import { apiService } from '../services/api'

// --- State ---
const conversations = ref<any[]>([])
const selectedConversation = ref<any>(null)
const messages = ref<any[]>([])
const inputMessage = ref('')
const isLoading = ref(false)
const searchQuery = ref('')
const showMobileSidebar = ref(false)
const messagesContainer = ref<HTMLElement>()
const showTypeSelector = ref(false)
const selectedType = ref('consultation')
const conversationName = ref('')
const activeConversationType = ref('consultation') // Filter by type, default to consultation

// Conversation types (removed 'all' option)
const conversationTypes = [
  { id: 'consultation', name: 'استشارات قانونية', icon: '⚖️' },
  { id: 'petitions', name: 'كتابة لوائح', icon: '📝' },
  { id: 'templates', name: 'كتابة نماذج', icon: '📋' },
  { id: 'documents', name: 'تحليل وثائق', icon: '📄' },
  { id: 'cases', name: 'دراسة دعاوي', icon: '🔍' },
  { id: 'contracts', name: 'عقود', icon: '📑' },
]

// --- LiveKit State ---
const room = ref<Room | null>(null)
const isLiveKitConnected = ref<boolean>(false)
const isReconnecting = ref<boolean>(false)
const reconnectionAttempts = ref<number>(0)

const isAITyping = ref(false);
const aiStatusMessage = ref(""); // المتغير الجديد لحالة المستشار

// --- Chat Logic ---
const handleIncomingDelta = (content: string) => {
  if (messages.value.length === 0) return;

  const lastMessage = messages.value[messages.value.length - 1];
  
  if (lastMessage.role === 'assistant') {
    lastMessage.content += content;
  } else {
    messages.value.push({
      role: 'assistant',
      content: content,
      id: Date.now()
    });
  }
};

// --- Updated connectToLiveKit ---
const connectToLiveKit = async (roomName: string) => {
  try {
    const { token, url } = await apiService.joinLiveKitRoom(roomName);
    
    if (room.value) {
      await room.value.disconnect();
    }

    room.value = new Room({
      adaptiveStream: true,
      dynacast: true,
      reconnectPolicy: {
        nextRetryDelayInMs: (context) => {
          console.log("Retrying connection...", context.retryCount);
          return 1000;
        }
      }
    });
    
    room.value
      .on(RoomEvent.Connected, () => {
        isLiveKitConnected.value = true;
        console.log("✅ Connected to LiveKit");
      })
      .on(RoomEvent.Disconnected, () => {
        isLiveKitConnected.value = false;
        isAITyping.value = false;
      })
      .on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        const decoder = new TextDecoder();
        try {
          const data = JSON.parse(decoder.decode(payload));
          console.log("📥 Received Type:", data.type);

          // معالجة الأخطاء (Errors)
          if (data.type === 'error') {
            isAITyping.value = false;
            aiStatusMessage.value = "";
            const errorMsg = data.message || "حدث خطأ غير معروف";
            console.error("❌ Error from AI:", errorMsg);
            
            messages.value.push({
              id: 'err-' + Date.now(),
              role: 'assistant',
              content: errorMsg,
              isError: true,
              createdAt: new Date()
            });
          }

          // معالجة الرسائل الكاملة (Complete messages)
          else if (data.type === 'message') {
            console.log("✅ Adding complete message:", data.content);
            messages.value.push({
              id: Date.now().toString(),
              role: 'assistant',
              content: data.content || '',
              createdAt: new Date()
            });
            isAITyping.value = false;
          }

          // معالجة مؤشر الكتابة (Typing indicator)
          else if (data.type === 'typing') {
            console.log("✍️ AI is typing...");
            isAITyping.value = true;
          }

          // معالجة إشارات الحالة (Thinking/Searching)
          else if (data.type === 'status') {
            isAITyping.value = true;
            const content = data.content || '';
            
            // Map status to Arabic messages
            let statusMessage = 'المستشار يحلل سؤالك...';
            if (content === 'searching_laws') {
              statusMessage = 'جاري البحث في مراجع القانون الأردني...';
            } else if (content === 'thinking') {
              statusMessage = 'جاري صياغة الإجابة...';
            }
            
            aiStatusMessage.value = statusMessage;
          }

          // معالجة المواد القانونية
          else if (data.type === 'article') {
            isAITyping.value = true;
            aiStatusMessage.value = "تم العثور على مواد قانونية ذات صلة...";
            
            const articleData = data.data;
            if (articleData) {
              const lawName = articleData.law || 'قانون غير محدد';
              const articleNumber = articleData.article || 'غير محدد';
              const content = articleData.text || '';
              
              // Combine all information
              const articleText = `📍 ${lawName} - مادة ${articleNumber}\n\n${content}`;
              
              messages.value.push({
                id: 'ref-' + Date.now(),
                role: 'assistant',
                content: articleText,
                isReference: true,
                createdAt: new Date()
              });
            }
          }

          // معالجة النص الحي (Streaming content - refined or delta)
          else if (data.type === 'refined' || data.type === 'delta') {
            isAITyping.value = true;
            aiStatusMessage.value = "المستشار يكتب الآن...";
            const textChunk = data.content || "";
            
            if (textChunk) {
              const lastMsg = messages.value[messages.value.length - 1];

              // Append to last assistant message if it's not a reference
              if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isReference !== true) {
                lastMsg.content += textChunk;
              } else {
                // Create new assistant message
                messages.value.push({
                  id: Date.now().toString(),
                  role: 'assistant',
                  content: textChunk,
                  createdAt: new Date()
                });
              }
            }
          }

          // معالجة نهاية البث
          else if (data.type === 'end') {
            isAITyping.value = false;
            aiStatusMessage.value = "";
          }

          // Fallback: Handle {message: "..."} format
          else if (data.message && !data.type) {
            console.log("✅ Adding message from fallback format");
            messages.value.push({
              id: Date.now().toString(),
              role: 'assistant',
              content: data.message,
              createdAt: new Date()
            });
            isAITyping.value = false;
          }

          // Fallback: Handle {data: {...}, type: "..."} format
          else if (data.data && typeof data.data === 'object') {
            const innerData = data.data;
            const type = data.type || innerData.type;
            
            if (innerData.text) {
              console.log("✅ Adding message from nested data format");
              messages.value.push({
                id: Date.now().toString(),
                role: 'assistant',
                content: innerData.text,
                createdAt: new Date()
              });
              isAITyping.value = false;
            }
          }

          scrollToBottom();
        } catch (e) {
          console.error("Error parsing AI data:", e);
        }
      });

    await room.value.connect(url, token);
  } catch (e) {
    console.error("LiveKit Connection Error:", e);
  }
};

const sendMessage = async () => {
  const currentConv = selectedConversation.value
  const text = inputMessage.value.trim()

  if (!text || !currentConv) return
  
  const roomName = `room_${currentConv.id}`
  
  inputMessage.value = ''
  isLoading.value = true
  isAITyping.value = true; // إظهار حالة جاري الكتابة فوراً
  aiStatusMessage.value = "جاري إرسال طلبك...";

  messages.value.push({ 
    id: Date.now(), 
    role: 'user', 
    content: text 
  })
  scrollToBottom()

  try {
    await apiService.sendMessageToLiveKit(roomName, text)
  } catch (e) {
    console.error('Error sending message:', e)
    isAITyping.value = false;
  } finally {
    isLoading.value = false
  }
}

// ... بقية الوظائف (selectConversation, fetchMessages, createNewConversation, etc.) ...
const selectConversation = async (conv: any) => {
  try {
    selectedConversation.value = conv
    messages.value = []
    const roomId = conv.id 
    const roomName = `room_${roomId}`
    await connectToLiveKit(roomName)
    await fetchMessages(roomId)
  } catch (error) {
    console.error('Error selecting conversation:', error)
  }
}

const fetchMessages = async (conversationId: string) => {
  isLoading.value = true;
  try {
    const response = await apiService.getConversationMessages(conversationId);
    console.log('📥 Fetched messages response:', response);
    
    // Backend returns { "history": messages, "conversation_id": id }
    if (response && Array.isArray(response.history)) {
      console.log('✅ Using response.history:', response.history.length, 'messages');
      messages.value = response.history;
    } else if (Array.isArray(response)) {
      console.log('✅ Using response as array:', response.length, 'messages');
      messages.value = response;
    } else if (response && Array.isArray(response.messages)) {
      console.log('✅ Using response.messages:', response.messages.length, 'messages');
      messages.value = response.messages;
    } else {
      console.warn('⚠️ No messages found in response');
      messages.value = [];
    }
    
    console.log('💬 Messages array:', messages.value.length, 'messages');
    await nextTick();
    scrollToBottom();
  } catch (error) {
    console.error("❌ Error loading history:", error);
    messages.value = []; 
  } finally {
    isLoading.value = false;
  }
};

const createNewConversation = async () => {
  // Show type selector instead of prompt
  showTypeSelector.value = true
  selectedType.value = 'consultation' // Default
  conversationName.value = ''
}

const selectConversationType = (typeId: string) => {
  selectedType.value = typeId
}

const confirmCreateConversation = async () => {
  // Validate name
  if (!conversationName.value || conversationName.value.trim() === '') {
    alert('الرجاء إدخال اسم المحادثة')
    return
  }
  
  try {
    const res = await apiService.createConversation(
      conversationName.value.trim(),
      selectedType.value
    )
    if (res.conversation) {
      conversations.value.unshift(res.conversation)
      messages.value = []
      selectedConversation.value = res.conversation
      await nextTick()
      const roomName = `room_${res.conversation.id}`
      await connectToLiveKit(roomName)
      await fetchMessages(res.conversation.id)
    }
    // Close modal
    showTypeSelector.value = false
  } catch (error) {
    console.error('Error creating conversation:', error)
    alert('فشل في إنشاء المحادثة')
  }
}

const cancelCreateConversation = () => {
  showTypeSelector.value = false
}

const loadConversations = async () => {
  try {
    const res = await apiService.getLiveKitConversations(activeConversationType.value)
    conversations.value = res.conversations || []
  } catch (error) {
    console.error('Error loading conversations:', error)
    conversations.value = []
  }
}

const filteredConversations = computed(() => {
  return conversations.value.filter(c => 
    c.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const scrollToBottom = () => nextTick(() => {
  if (messagesContainer.value) messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
})

const formatTime = (d: any) => new Date(d).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })

const manualReconnect = async () => {
  if (selectedConversation.value) {
    const roomName = `room_${selectedConversation.value.id}`
    await connectToLiveKit(roomName)
  }
}

onMounted(async () => {
  await loadConversations()
})

onUnmounted(() => room.value?.disconnect())
</script>

<template>
  <div class="flex h-screen bg-gray-50 overflow-hidden font-arabic" dir="rtl">
    <!-- Conversation Type Selector Modal -->
    <div v-if="showTypeSelector" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <!-- Header -->
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-800">محادثة جديدة</h2>
          <p class="text-sm text-gray-600 mt-1">اختر نوع المحادثة وأدخل الاسم</p>
        </div>
        
        <!-- Content -->
        <div class="p-6 space-y-6">
          <!-- Conversation Name Input -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">اسم المحادثة</label>
            <input 
              v-model="conversationName" 
              type="text" 
              placeholder="مثال: استشارة عقد إيجار"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-right"
              dir="rtl"
              @keypress.enter="confirmCreateConversation"
            />
          </div>
          
          <!-- Conversation Type Selection -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-3">نوع المحادثة</label>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div 
                v-for="type in conversationTypes" 
                :key="type.id"
                @click="selectConversationType(type.id)"
                :class="[
                  'p-4 rounded-xl cursor-pointer border-2 transition-all duration-200 hover:shadow-md',
                  selectedType === type.id 
                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <div class="text-center">
                  <div :class="['text-3xl mb-2 p-2 rounded-lg', type, 'text-white inline-block']">
                    {{ type.icon }}
                  </div>
                  <h3 class="font-semibold text-sm text-gray-800">{{ type.name }}</h3>
                  <div v-if="selectedType === type.id" class="mt-2">
                    <span class="text-xs text-blue-600 font-medium">✓ محدد</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer Buttons -->
        <div class="p-6 border-t border-gray-200 flex gap-3">
          <button 
            @click="cancelCreateConversation" 
            class="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors"
          >
            إلغاء
          </button>
          <button 
            @click="confirmCreateConversation" 
            class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="!conversationName.trim()"
          >
            إنشاء المحادثة
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile Sidebar Overlay -->
    <div v-if="showMobileSidebar" class="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" @click="showMobileSidebar = false"></div>
    
    <!-- Sidebar -->
    <div :class="['bg-white border-l border-gray-200 flex flex-col flex-shrink-0 fixed md:relative h-full z-40 transform transition-transform duration-300 ease-in-out', 
                     showMobileSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0', 
                     'w-80 md:w-80']">
      <div class="p-4 md:p-6 border-b border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg md:text-xl font-bold text-gray-800">المحادثات</h2>
          <div class="flex items-center gap-2">
            <button @click="createNewConversation" class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Plus class="w-5 h-5 text-gray-600" />
            </button>
            <button @click="showMobileSidebar = false" class="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X class="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        <!-- Conversation Type Dropdown -->
        <div class="mb-4">
          <label class="block text-xs font-semibold text-gray-600 mb-2">نوع المحادثات</label>
          <select 
            v-model="activeConversationType" 
            @change="loadConversations"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm cursor-pointer"
          >
            <option v-for="type in conversationTypes" :key="type.id" :value="type.id">
              {{ type.icon }} {{ type.name }}
            </option>
          </select>
        </div>
        
        <!-- Search -->
        <div class="relative">
          <Search class="absolute right-3 top-3 w-4 h-4 text-gray-400" />
          <input v-model="searchQuery" type="text" placeholder="بحث..." class="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div v-for="conv in filteredConversations" :key="conv.id" @click="selectConversation(conv); showMobileSidebar = false"
          :class="['p-3 md:p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer', selectedConversation?.id === conv.id ? 'bg-blue-50 border-blue-200' : '']">
          <h3 class="font-semibold text-sm truncate">{{ conv.title }}</h3>
          <p class="text-xs text-gray-500">{{ formatTime(conv.updated_at) }}</p>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col min-w-0 relative">
      <!-- Mobile Header -->
      <header class="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4 sticky top-0 z-20 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3 space-x-reverse">
            <!-- Mobile Menu Toggle -->
            <button @click="showMobileSidebar = true" class="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Menu class="w-5 h-5 text-gray-600" />
            </button>
            
            <div class="relative">
              <div class="bg-blue-100 p-2 rounded-lg"><MessageSquare class="w-5 h-5 text-blue-600" /></div>
              <div v-if="selectedConversation" class="absolute -bottom-1 -left-1 w-3 h-3 rounded-full border-2 border-white"
                :class="isLiveKitConnected ? 'bg-green-500' : 'bg-red-500'"></div>
            </div>
            <div>
              <h1 class="text-base md:text-lg font-semibold text-gray-800">{{ selectedConversation?.title || 'مساعدك القانوني' }}</h1>
              <p class="text-xs text-gray-500">{{ isLiveKitConnected ? 'اتصال حي نشط' : 'بانتظار اختيار محادثة' }}</p>
            </div>
          </div>
          <button @click="manualReconnect" :disabled="!selectedConversation" class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <Video class="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </header>

      <!-- Messages Area -->
      <div class="flex-1 overflow-y-auto p-3 md:p-6 space-y-3 md:space-y-4" ref="messagesContainer">
        <div v-for="msg in messages" :key="msg.id" :class="['flex gap-2 md:gap-3', msg.role === 'user' ? 'flex-row-reverse' : 'flex-row']">
          <div :class="['p-3 md:p-4 rounded-2xl max-w-[85%] md:max-w-[80%] shadow-sm whitespace-pre-wrap', 
                       msg.role === 'user' ? 'bg-blue-600 text-white' : 
                       msg.isError ? 'bg-red-50 border-2 border-red-300 text-red-700' :
                       msg.isReference ? 'border-r-4 border-blue-400 bg-blue-50 text-gray-800' : 
                       'bg-white border text-gray-800']">
            <p class="text-sm md:text-base">{{ msg.content }}</p>
            <p v-if="msg.createdAt" class="text-xs mt-1 opacity-70" :class="msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'">
              {{ formatTime(msg.createdAt) }}
            </p>
          </div>
        </div>

        <div v-if="isAITyping" class="flex gap-2 md:gap-3 flex-row">
          <div class="bg-gray-100 border text-gray-600 p-3 md:p-4 rounded-2xl flex items-center gap-3">
            <div class="flex gap-1">
              <span class="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></span>
              <span class="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
              <span class="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
            </div>
            <span class="text-sm font-medium">{{ aiStatusMessage || 'المستشار يحلل سؤالك...' }}</span>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div v-if="selectedConversation" class="p-3 md:p-4 bg-white border-t border-gray-200">
        <div class="max-w-4xl mx-auto flex items-end gap-2">
          <textarea v-model="inputMessage" @keypress.enter.prevent="sendMessage" placeholder="اكتب سؤالك..." 
            class="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none resize-none max-h-32 focus:ring-2 focus:ring-blue-500 text-sm md:text-base"></textarea>
          <button @click="sendMessage" :disabled="isLoading" class="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300">
            <Send class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-arabic {
  font-family: 'Tajawal', sans-serif;
}
</style>